import { ChangeEvent, useMemo, useRef } from 'react';
import { useAppSelector } from '@shared/store';
import { usePlayerContext } from 'application/providers/PlayerProvider';
import { ACCEPT_AUDIO_FILETYPES, ACCEPT_VIDEO_FILETYPES } from 'application/pages/constants';
import CommonUtils from 'application/utils/CommonUtils';
import { AdCampaignMediaTypeEnum } from '@shared/api/graphql/generated';
import { AdCampaignTypeEnum } from '@entities/media';
import { MediaUploaderItemType, MediaUploaderItemValue } from '@zvuk-b2b/react-uikit/ui/MediaUploader';
import { TIME_PATTERN } from '@zvuk-b2b/react-uikit/ui/TimeInput';
import { selectCampaignType } from '../../../slices/createAdCampaign/selectors';
import { toMediaUploaderItem } from '../../AdCampaignMedia/utils';
import { filterAcceptedVideoFiles } from './utils';
import { TEXTS, UPLOAD_BUTTON_TEXTS } from './texts';
import { UseAdCampaignMediaViewModelProps } from './types';

export const useAdCampaignMediaViewModel = ({
  mediaType = AdCampaignMediaTypeEnum.Audio,
  promoState,
  timeById,
  onTimeChange,
  onTimeRemove,
}: UseAdCampaignMediaViewModelProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const campaignType = useAppSelector(selectCampaignType);
  const player = usePlayerContext();
  const { data, upload, remove, retry, reorder } = promoState;

  const isExactTime = campaignType === AdCampaignTypeEnum.ExactTime;
  const isInstant = campaignType === AdCampaignTypeEnum.Instant;
  const isDraggable = !isInstant;
  const accept = mediaType === AdCampaignMediaTypeEnum.Audio ? ACCEPT_AUDIO_FILETYPES : ACCEPT_VIDEO_FILETYPES;
  const acceptValue = Object.values(accept).flat().join(',');
  const uploadButtonText = UPLOAD_BUTTON_TEXTS[mediaType];
  const uploadDescription = TEXTS.MEDIA_UPLOAD_DESC(CommonUtils.convertObjectValuesToString(accept));
  const items = isDraggable ? data.rawStatuses : data.statuses;
  const mediaUploadItems = useMemo(() => items.map(toMediaUploaderItem), [items]);

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  const handleFilesSelected = (files: File[]) => {
    const acceptedFiles = mediaType === AdCampaignMediaTypeEnum.Video ? filterAcceptedVideoFiles(files) : files;

    if (acceptedFiles.length > 0) {
      upload(acceptedFiles, mediaType);
    }
  };

  const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    if (files.length > 0) {
      handleFilesSelected(files);
    }

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleRemoveClick = (id: string) => {
    if (player.fileUuid === id) {
      player.stop?.();
    }

    remove(id);
    onTimeRemove(id);
  };

  const handleRetryClick = (id: string) => {
    retry(id, mediaType);
  };

  const handleRemoveMedia = (value: MediaUploaderItemValue) => {
    handleRemoveClick(value.id);
  };

  const handleRetryMedia = (value: MediaUploaderItemValue) => {
    handleRetryClick(value.id);
  };

  const handleVideoMediaReorder = (reorderedItems: MediaUploaderItemType[]) => {
    const statusesById = new Map(items.map((item) => [item.id, item]));
    const reorderedStatuses = reorderedItems
      .map((item) => statusesById.get(item.value.id))
      .filter((item): item is (typeof items)[number] => Boolean(item));
    const reorderedStatusIds = new Set(reorderedStatuses.map((item) => item.id));
    const isSameOrder = reorderedStatuses.every((item, index) => item.id === items[index]?.id);

    if (reorderedStatuses.length === items.length && reorderedStatusIds.size === items.length && !isSameOrder) {
      reorder(reorderedStatuses);
    }
  };

  const handlePlayClick = (id: string, filename: string, url: string) => {
    player.play?.({ fileName: filename, fileUuid: id, fileUrl: url });
  };

  const handleTimeBlur = (id: string) => {
    const value = timeById[id];
    if (value !== undefined && !TIME_PATTERN.test(value)) {
      onTimeChange(id, '');
    }
  };

  const isUploadDisabled = isInstant && items.length > 0;

  const isNextDisabled =
    items.length === 0 ||
    items.some((item) => item.isLoading || item.error || !item.promoId) ||
    (isExactTime && items.some((item) => item.id && !TIME_PATTERN.test(timeById[item.id] ?? '')));

  return {
    inputRef,
    isExactTime,
    isDraggable,
    acceptValue,
    uploadButtonText,
    uploadDescription,
    items,
    player,
    mediaUploadItems,
    isUploadDisabled,
    isNextDisabled,
    handleUploadClick,
    handleFilesChange,
    handleFilesSelected,
    handleRemoveClick,
    handleRetryClick,
    handleRemoveMedia,
    handleRetryMedia,
    handleVideoMediaReorder,
    handlePlayClick,
    handleTimeBlur,
    reorder,
  };
};
